// API service for GoCompareAI backend
const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Helper for API calls
async function apiCall(endpoint, options = {}) {
  const url = `${API_BASE}/api${endpoint}`;
  
  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  };

  try {
    const response = await fetch(url, config);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `API error: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error(`API call failed: ${endpoint}`, error);
    throw error;
  }
}

// ============================================
// EDUCATION SECTIONS API
// ============================================

export const educationApi = {
  getAll: (publishedOnly = false) => 
    apiCall(`/education${publishedOnly ? '?published_only=true' : ''}`),
  
  create: (section) => 
    apiCall('/education', { method: 'POST', body: JSON.stringify(section) }),
  
  update: (sectionId, updates) => 
    apiCall(`/education/${sectionId}`, { method: 'PUT', body: JSON.stringify(updates) }),
  
  delete: (sectionId) => 
    apiCall(`/education/${sectionId}`, { method: 'DELETE' }),
};

// ============================================
// BLOG POSTS API (Admin)
// ============================================

export const blogApi = {
  // Admin endpoints for full CRUD
  getAll: (includeContent = false) => apiCall(`/admin/blog/posts?include_content=${includeContent}`),
  
  get: (postId) => apiCall(`/blog/posts/id/${postId}`),
  
  create: (post) => 
    apiCall('/admin/blog/posts', { method: 'POST', body: JSON.stringify(post) }),
  
  update: (postId, updates) => 
    apiCall(`/admin/blog/posts/${postId}`, { method: 'PUT', body: JSON.stringify(updates) }),
  
  delete: (postId) => 
    apiCall(`/admin/blog/posts/${postId}`, { method: 'DELETE' }),
  
  publish: (postId) =>
    apiCall(`/admin/blog/posts/${postId}/publish`, { method: 'POST' }),
  
  unpublish: (postId) =>
    apiCall(`/admin/blog/posts/${postId}/unpublish`, { method: 'POST' }),
  
  // Public endpoints
  getPublished: (limit = 20, offset = 0) => 
    apiCall(`/blog/posts?limit=${limit}&offset=${offset}`),
  
  getBySlug: (slug) => apiCall(`/blog/posts/${slug}`),
};

// ============================================
// DISPLAY SETTINGS API
// ============================================

export const settingsApi = {
  getDisplay: () => apiCall('/settings/display'),
  
  updateDisplay: (settings) => 
    apiCall('/settings/display', { method: 'PUT', body: JSON.stringify(settings) }),
};

// ============================================
// SAVED RESULTS API
// ============================================

export const resultsApi = {
  save: (data) => 
    apiCall('/results/save', { method: 'POST', body: JSON.stringify(data) }),
  
  get: (shareCode) => 
    apiCall(`/results/${shareCode}`),
  
  delete: (shareCode) => 
    apiCall(`/results/${shareCode}`, { method: 'DELETE' }),
};

// ============================================
// INITIALIZATION API
// ============================================

export const initApi = {
  seed: () => apiCall('/init/seed', { method: 'POST' }),
};

// ============================================
// SUGGESTIONS API
// ============================================

export const suggestionsApi = {
  submit: (suggestion) => 
    apiCall('/suggestions', { method: 'POST', body: JSON.stringify(suggestion) }),
  
  getAll: () => apiCall('/admin/suggestions'),
  
  updateStatus: (suggestionId, status) => 
    apiCall(`/admin/suggestions/${suggestionId}/status`, { 
      method: 'PUT', 
      body: JSON.stringify({ status }) 
    }),
  
  createVendor: (suggestionId) =>
    apiCall(`/admin/suggestions/${suggestionId}/create-vendor`, { method: 'POST' }),
};

// ============================================
// USE CASE SUGGESTIONS API (for "Not sure" feature)
// ============================================

export const useCaseSuggestionsApi = {
  suggest: (text) => 
    apiCall('/suggest-use-cases', { method: 'POST', body: JSON.stringify({ text }) }),
  
  // AI-powered platform recommendations
  aiRecommend: (query) =>
    apiCall('/ai-recommend', { method: 'POST', body: JSON.stringify({ query }) }),
};

// ============================================
// FLAGGED QUERIES API (Admin moderation)
// ============================================

export const flaggedQueriesApi = {
  getAll: (reviewed = null, limit = 50) => {
    let url = `/admin/flagged-queries?limit=${limit}`;
    if (reviewed !== null) url += `&reviewed=${reviewed}`;
    return apiCall(url);
  },
  
  review: (queryId, adminNotes = null) =>
    apiCall(`/admin/flagged-queries/${queryId}/review${adminNotes ? `?admin_notes=${encodeURIComponent(adminNotes)}` : ''}`, { method: 'PUT' }),
  
  delete: (queryId) =>
    apiCall(`/admin/flagged-queries/${queryId}`, { method: 'DELETE' }),
};

// ============================================
// ANALYTICS API
// ============================================

export const analyticsApi = {
  track: (eventType, eventData = {}) => 
    apiCall('/analytics/track', { 
      method: 'POST', 
      body: JSON.stringify({ event_type: eventType, event_data: eventData }) 
    }),
  
  // Track vendor-specific analytics (profile views, comparisons, clicks)
  trackVendor: (eventType, platformId, source = 'unknown') => {
    // Get or create session ID for deduplication
    let sessionId = sessionStorage.getItem('analytics_session');
    if (!sessionId) {
      sessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('analytics_session', sessionId);
    }
    
    return apiCall('/analytics/vendor-track', {
      method: 'POST',
      body: JSON.stringify({
        event_type: eventType,
        platform_id: platformId,
        source: source,
        session_id: sessionId
      })
    }).catch(() => {}); // Silent fail for analytics
  },
  
  getSummary: (startDate = null, endDate = null) => {
    let url = '/admin/analytics';
    const params = [];
    if (startDate) params.push(`start_date=${startDate}`);
    if (endDate) params.push(`end_date=${endDate}`);
    if (params.length > 0) url += '?' + params.join('&');
    return apiCall(url);
  },
};

// ============================================
// ADMIN PLATFORMS API (legacy metadata)
// ============================================

export const adminPlatformsApi = {
  getAll: () => apiCall('/admin/platforms'),
  
  get: (platformId) => apiCall(`/admin/platforms/${platformId}`),
  
  update: (platformId, updates) => 
    apiCall(`/admin/platforms/${platformId}`, { 
      method: 'POST', 
      body: JSON.stringify(updates) 
    }),
};

// ============================================
// PLATFORMS API (full CRUD)
// ============================================

export const platformsApi = {
  getAll: (enabledOnly = false) => 
    apiCall(`/platforms${enabledOnly ? '?enabled_only=true' : ''}`),
  
  get: (platformId) => apiCall(`/platforms/${platformId}`),
  
  create: (platform) => 
    apiCall('/platforms', { method: 'POST', body: JSON.stringify(platform) }),
  
  update: (platformId, updates) => 
    apiCall(`/platforms/${platformId}`, { method: 'PUT', body: JSON.stringify(updates) }),
  
  delete: (platformId) => 
    apiCall(`/platforms/${platformId}`, { method: 'DELETE' }),
  
  seed: (platformsData) => 
    apiCall('/platforms/seed', { method: 'POST', body: JSON.stringify(platformsData) }),
  
  getCount: () => apiCall('/platforms/count'),
};

// ============================================
// VENDOR API (Admin)
// ============================================

export const vendorAdminApi = {
  getAll: () => apiCall('/admin/vendors'),
  get: (vendorId) => apiCall(`/admin/vendors/${vendorId}`),
  create: (vendor) => apiCall('/admin/vendors', { method: 'POST', body: JSON.stringify(vendor) }),
  update: (vendorId, updates) => apiCall(`/admin/vendors/${vendorId}`, { method: 'PUT', body: JSON.stringify(updates) }),
  delete: (vendorId) => apiCall(`/admin/vendors/${vendorId}`, { method: 'DELETE' }),
  sendInvite: (vendorId, adminName = 'GoCompareAI Team') => 
    apiCall(`/admin/vendors/${vendorId}/send-invite?admin_name=${encodeURIComponent(adminName)}`, { method: 'POST' }),
  revokeInvite: (vendorId) => apiCall(`/admin/vendors/${vendorId}/revoke-invite`, { method: 'POST' }),
  // Quick tier upgrade
  upgradeTier: (vendorId, tier, paymentReference = null) => 
    apiCall(`/admin/vendors/${vendorId}/upgrade-tier`, { 
      method: 'POST', 
      body: JSON.stringify({ tier, payment_reference: paymentReference }) 
    }),
  getPendingEdits: (status = 'pending') => apiCall(`/admin/vendor-edits?status=${status}`),
  reviewEdit: (editId, review, adminName = 'Admin') => 
    apiCall(`/admin/vendor-edits/${editId}/review?admin_name=${encodeURIComponent(adminName)}`, { 
      method: 'PUT', 
      body: JSON.stringify(review) 
    }),
  // Bulk operations
  bulkCreateFromPlatforms: () => apiCall('/admin/vendors/bulk-create-from-platforms', { method: 'POST' }),
  getUnlinkedPlatforms: () => apiCall('/admin/vendors/unlinked-platforms'),
  // Platform link requests
  getLinkRequests: (status = 'pending') => apiCall(`/admin/platform-link-requests?status=${status}`),
  approveLinkRequest: (requestId, newPlatformId = null) => 
    apiCall(`/admin/platform-link-requests/${requestId}/approve`, { 
      method: 'POST', 
      body: JSON.stringify(newPlatformId ? { new_platform_id: newPlatformId } : {}) 
    }),
  rejectLinkRequest: (requestId, adminNotes) => 
    apiCall(`/admin/platform-link-requests/${requestId}/reject`, { 
      method: 'POST', 
      body: JSON.stringify({ admin_notes: adminNotes }) 
    }),
};

// ============================================
// EMAIL TEMPLATES API (Admin)
// ============================================

export const emailTemplatesApi = {
  getAll: () => apiCall('/admin/email-templates'),
  get: (templateId) => apiCall(`/admin/email-templates/${templateId}`),
  update: (templateId, updates) => 
    apiCall(`/admin/email-templates/${templateId}`, { method: 'PUT', body: JSON.stringify(updates) }),
};

// ============================================
// VENDOR PORTAL API
// ============================================

const getVendorToken = () => {
  return localStorage.getItem('vendor_token');
};

const vendorApiCall = async (endpoint, options = {}) => {
  const token = getVendorToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
    ...options.headers,
  };
  
  const response = await fetch(`${API_BASE}/api${endpoint}`, {
    ...options,
    headers,
  });
  
  if (!response.ok) {
    // Handle 401 specifically for better error messages
    if (response.status === 401) {
      // Clear invalid token
      localStorage.removeItem('vendor_token');
      localStorage.removeItem('vendor_info');
      throw new Error('401 - Session expired. Please log in again.');
    }
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || `API error: ${response.status}`);
  }
  
  return response.json();
};

export const vendorApi = {
  validateToken: (token) => apiCall(`/vendor/validate-token?token=${token}`),
  validateResetToken: (token) => apiCall(`/vendor/validate-reset-token?token=${token}`),
  activate: (token, password) => apiCall('/vendor/activate', { method: 'POST', body: JSON.stringify({ token, password }) }),
  login: (email, password) => apiCall('/vendor/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  logout: () => vendorApiCall('/vendor/logout', { method: 'POST' }),
  forgotPassword: (email) => apiCall('/vendor/forgot-password', { method: 'POST', body: JSON.stringify({ email }) }),
  resetPassword: (token, password) => apiCall('/vendor/reset-password', { method: 'POST', body: JSON.stringify({ token, password }) }),
  getMe: () => vendorApiCall('/vendor/me'),
  getTierPermissions: () => vendorApiCall('/vendor/tier-permissions'),
  getPlatforms: () => vendorApiCall('/vendor/platforms'),
  getPlatform: (platformId) => vendorApiCall(`/vendor/platforms/${platformId}`),
  submitEdit: (platformId, fieldChanges) => 
    vendorApiCall(`/vendor/platforms/${platformId}/edit`, { method: 'POST', body: JSON.stringify({ platform_id: platformId, field_changes: fieldChanges }) }),
  getEdits: () => vendorApiCall('/vendor/edits'),
  // Primary platform
  setPrimaryPlatform: (platformId) => 
    vendorApiCall('/vendor/primary-platform', { method: 'PUT', body: JSON.stringify({ platform_id: platformId }) }),
  // Platform link requests
  createLinkRequest: (requestData) => 
    vendorApiCall('/vendor/platform-link-requests', { method: 'POST', body: JSON.stringify(requestData) }),
  getLinkRequests: () => vendorApiCall('/vendor/platform-link-requests'),
  // Analytics
  getAnalyticsSummary: (period = 30, platformId = 'all') => 
    vendorApiCall(`/vendor/analytics/summary?period=${period}&platform_id=${platformId}`),
  getAnalyticsViews: (period = 30, platformId = 'all') => 
    vendorApiCall(`/vendor/analytics/views?period=${period}&platform_id=${platformId}`),
};

// Public tier info (no auth required)
export const tierApi = {
  getAllTiers: () => apiCall('/vendor/tiers'),
  getPlatformVendorInfo: (platformId) => apiCall(`/platforms/${platformId}/vendor-info`),
};

// Admin tier management
export const tierAdminApi = {
  getAllTierConfigs: () => apiCall('/admin/vendor-tiers'),
};

export default {
  education: educationApi,
  blog: blogApi,
  settings: settingsApi,
  results: resultsApi,
  init: initApi,
  suggestions: suggestionsApi,
  analytics: analyticsApi,
  adminPlatforms: adminPlatformsApi,
  platforms: platformsApi,
  vendorAdmin: vendorAdminApi,
  emailTemplates: emailTemplatesApi,
  vendor: vendorApi,
  tiers: tierApi,
  tierAdmin: tierAdminApi,
};
